(() => {
  if (window.__bitcoProductBound) return;
  window.__bitcoProductBound = true;

  // See sections/bitco-main-product.liquid's header comment above the sticky-bar markup for the
  // full rationale behind each decision below (price sync via Dawn's own PUB_SUB_EVENTS instead
  // of a MutationObserver, no second <product-form>, prefers-reduced-motion handling, etc).

  const qs = (s, r) => (r || document).querySelector(s);

  const mql = window.matchMedia('(max-width: 989px)');

  // Re-created on every (re)init so a stale reference never keeps watching a detached node after
  // shopify:section:load swaps the section's DOM.
  let atcObserver = null;
  let variantChangeUnsubscribe = null;

  // Conservative default: assume the primary button IS in view until the IntersectionObserver's
  // first callback says otherwise, so the bar never flashes visible before that first check.
  let primaryVisible = true;

  const getBar = () => qs('[data-bitco-sticky-atc]');
  const getPrimaryBtn = () => qs('[data-bitco-atc-primary]');

  const updateBarVisibility = () => {
    const bar = getBar();
    if (!bar) return;
    bar.hidden = !(mql.matches && !primaryVisible);
    // The mobile sticky bottom bar (snippets/bitco-tabbar.liquid) occupies the same edge of the
    // screen. Rather than stacking both, this class slides it out while the add-to-cart bar owns
    // the bottom — the CSS side lives in assets/bitco-base.css (.bitco-atc-bar-visible).
    document.body.classList.toggle('bitco-atc-bar-visible', !bar.hidden);
  };

  const onIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    primaryVisible = entry.isIntersecting;
    updateBarVisibility();
  };

  const syncPriceFrom = (variantEvent) => {
    const bar = getBar();
    if (!bar) return;
    // Target the `.bitco-price` wrapper itself (rendered inside the slot by
    // `{% render 'bitco-price', context: 'sticky' %}`) and mutate its contents in place — the
    // wrapper's own `.bitco-price`/`.bitco-price--sticky` classes MUST survive, because the VAT
    // toggle CSS (`body[data-vat-display] .bitco-price [data-vat]`) and the sticky compact/
    // was-price-hiding rules all key off that wrapper. Copying `sourcePrice.innerHTML` into the
    // OUTER slot instead would strip the wrapper and break both.
    const destPrice = bar.querySelector('.bitco-price');
    if (!destPrice) return;

    const sectionId = (variantEvent && variantEvent.data && variantEvent.data.sectionId) || bar.dataset.sectionId;
    const source = sectionId && document.getElementById(`price-${sectionId}`);
    const sourcePrice = source && source.querySelector('.bitco-price');
    if (sourcePrice) destPrice.innerHTML = sourcePrice.innerHTML;
  };

  const syncSubmitState = (variantEvent) => {
    const btn = qs('[data-bitco-sticky-atc-submit]');
    const variant = variantEvent && variantEvent.data && variantEvent.data.variant;
    if (!btn || !variant) return;
    btn.disabled = !variant.available;
  };

  // ---- M1.1: don't pre-select a size -------------------------------------------------
  // Shopify renders `product.selected_or_first_available_variant` checked, so a footwear PDP
  // opened cold arrived with size 35 already chosen — and the sticky bar (title + price + Add to
  // cart, no size shown) would silently add it. On a 14-size range that is a wrong-size order
  // waiting to happen. So on a cold load the size radios are cleared and the buy controls are
  // held until the shopper picks one.
  //
  // Deliberately done in JS rather than Liquid: Dawn's product-info.js re-renders the picker and
  // the buy buttons from the server on every option change, and the server always resolves SOME
  // variant. Gating here means we hold exactly one state — "nothing chosen yet, this page load"
  // — and hand back to Dawn's untouched machinery the moment a size is clicked.
  let sizeChosen = false;
  let sizePending = false;

  const getSizeGroup = () => {
    const options = qs('.bitco-variant-options--size');
    return options ? options.closest('.bitco-variant-group') : null;
  };
  const getForm = () => qs('.bitco-pdp__form');
  const getPrimaryLabel = () => {
    const btn = getPrimaryBtn();
    return btn && btn.querySelector('span');
  };

  const setSizePending = (pending) => {
    sizePending = pending;
    const form = getForm();
    const label = getPrimaryLabel();
    const primary = getPrimaryBtn();
    const stickyBtn = qs('[data-bitco-sticky-atc-submit]');

    // Drives the CSS: dims the two add buttons and hides the express-payment button, which would
    // otherwise take a shopper straight to checkout with the un-chosen default variant.
    document.body.classList.toggle('bitco-size-pending', pending);

    [primary, stickyBtn].forEach((btn) => {
      if (!btn) return;
      // aria-disabled, not disabled: a truly disabled button reads as "sold out", and Dawn owns
      // the real `disabled` attribute (it toggles it on every variant swap).
      if (pending) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });

    if (label && form) {
      if (pending) {
        if (label.dataset.bitcoLabelWas === undefined) label.dataset.bitcoLabelWas = label.textContent;
        label.textContent = form.dataset.selectSizeLabel || label.textContent;
      } else if (label.dataset.bitcoLabelWas !== undefined) {
        label.textContent = label.dataset.bitcoLabelWas;
        delete label.dataset.bitcoLabelWas;
      }
    }
    if (stickyBtn && form) {
      if (pending) {
        if (stickyBtn.dataset.bitcoLabelWas === undefined) stickyBtn.dataset.bitcoLabelWas = stickyBtn.textContent;
        stickyBtn.textContent = form.dataset.selectSizeLabel || stickyBtn.textContent;
      } else if (stickyBtn.dataset.bitcoLabelWas !== undefined) {
        stickyBtn.textContent = stickyBtn.dataset.bitcoLabelWas;
        delete stickyBtn.dataset.bitcoLabelWas;
      }
    }
  };

  const clearPreselectedSize = () => {
    if (sizeChosen) return;
    const group = getSizeGroup();
    if (!group) return;

    const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
    // A single-size product has nothing to choose — leave it exactly as the server rendered it.
    if (radios.length < 2) return;

    // NOTE: this deliberately clears the size even when the URL carries ?variant=. That
    // exemption was wrong: a colour swatch on a collection card links to ?variant=…, so
    // arriving that way encodes a COLOUR choice and Shopify resolves a full variant behind it —
    // which silently picked a size too. The sticky bar would then read "schwarz/pink · 38" with
    // a live Add to cart for a size nobody chose. Colour still comes through (the server marks
    // that option selected); only the size is asked for again. Shipping the wrong size costs far
    // more than asking a shopper who deep-linked to re-confirm one they can see is already there.
    radios.forEach((radio) => {
      radio.checked = false;
    });

    const form = getForm();
    const readout = group.querySelector('[data-selected-value]');
    if (readout && form && form.dataset.chooseSizeLabel) readout.textContent = form.dataset.chooseSizeLabel;
    group.classList.add('bitco-variant-group--pending');
    setSizePending(true);
  };

  // The size grid is a <details> (snippets/bitco-variant-picker.liquid) so it can collapse to a
  // single row on mobile. Rendered `open` for the no-JS case; below 750px it starts closed and
  // closes again the moment a size is picked, so the grid is only ever on screen while it is
  // actually being used. Driven by matchMedia rather than a resize handler so an orientation
  // change fixes itself, and only written on a breakpoint change so it never fights a shopper
  // who opened it by hand.
  const foldMql = window.matchMedia('(max-width: 749px)');
  const getFold = () => qs('[data-bitco-variant-fold]');
  const syncFold = () => {
    const fold = getFold();
    if (!fold) return;
    fold.open = !foldMql.matches;
  };

  const onSizeChosen = () => {
    sizeChosen = true;
    const group = getSizeGroup();
    if (group) group.classList.remove('bitco-variant-group--pending');
    setSizePending(false);
    const fold = getFold();
    // Collapsing on desktop would hide the grid for no reason — there it is always open.
    if (fold && foldMql.matches) fold.open = false;
  };

  // Nudge the shopper to the thing they still have to do, rather than a dead tap. Opening the
  // fold matters as much as scrolling to it: collapsed, "choose a size" is a row to tap, and a
  // shopper who just pressed Add to cart should land on the open grid, not on another closed
  // control.
  const revealSizeGroup = () => {
    const group = getSizeGroup();
    if (!group) return;
    const fold = getFold();
    if (fold) fold.open = true;
    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    group.classList.remove('bitco-variant-group--nudge');
    // Reflow so the animation restarts if the shopper taps twice.
    void group.offsetWidth;
    group.classList.add('bitco-variant-group--nudge');
  };

  // ---- M1.1: mirror the chosen options into the sticky bar ----------------------------
  const syncStickyVariant = () => {
    const readout = qs('[data-bitco-sticky-variant]');
    if (!readout) return;
    const checked = Array.from(document.querySelectorAll('.bitco-variant-group input[type="radio"]:checked'));
    // Keyed by option name, last write wins: product-info.js swaps the picker by inserting the
    // freshly fetched markup before removing the old, so for one frame BOTH pickers are in the
    // DOM and a plain map() over :checked yields every value twice ("42 · BLACK · 42 · BLACK").
    // The re-render is also the last thing to happen, so there is no later call to tidy up.
    const byOption = new Map();
    checked.forEach((input) => {
      const key = input.dataset.optionName || input.name || input.value;
      if (input.value) byOption.set(key, input.value);
    });
    const values = Array.from(byOption.values());
    readout.textContent = values.join(' · ');
    readout.hidden = values.length === 0;
  };

  // == "Read more" clamps (snippets/bitco-clamp.liquid) ==
  // The collapsed state ships in the markup and the cap lives in CSS, so this code owns exactly
  // two decisions: whether the toggle is needed at all (content that already fits gets moved to
  // data-clamp="off", which hides the button and drops the fade), and the expand/collapse itself.
  // Nothing here writes inline styles.

  const measureClamp = (clamp) => {
    const content = clamp.querySelector('[data-bitco-clamp-content]');
    if (!content) return;
    // Already expanded by the shopper — re-collapsing under them on a resize would be hostile.
    if (clamp.dataset.clamp === 'expanded') return;
    // Not rendered (inside a closed <details>, or a hidden tab): every box metric reads 0, which
    // is indistinguishable from "content fits". Leave the state alone; the toggle listener below
    // re-measures the moment the <details> opens.
    if (content.offsetHeight === 0) return;

    // Measure against the capped box, so this is also correct when re-running on a clamp that a
    // previous pass moved to "off".
    clamp.dataset.clamp = 'collapsed';
    // 8px of slack: a content box one or two pixels over the cap is not worth a "Read more".
    const overflows = content.scrollHeight - content.clientHeight > 8;
    if (!overflows) clamp.dataset.clamp = 'off';
  };

  const measureAllClamps = () => {
    document.querySelectorAll('[data-bitco-clamp]').forEach(measureClamp);
  };

  const toggleClamp = (btn) => {
    const clamp = btn.closest('[data-bitco-clamp]');
    if (!clamp) return;
    const wasExpanded = clamp.dataset.clamp === 'expanded';

    if (wasExpanded) {
      // Collapsing can pull the top of the block above the viewport — the shopper clicked a
      // button that then jumps somewhere off-screen. Pin the block's top back into view first.
      const top = clamp.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollBy({
          top,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    clamp.dataset.clamp = wasExpanded ? 'collapsed' : 'expanded';
    btn.setAttribute('aria-expanded', String(!wasExpanded));
    const label = btn.querySelector('[data-bitco-clamp-label]');
    if (label) label.textContent = wasExpanded ? btn.dataset.labelMore : btn.dataset.labelLess;
  };

  // The `toggle` event does not bubble, so this has to be a capture-phase document listener
  // rather than one per <details>. Cheap, and it survives Dawn re-rendering the section.
  const onDocumentToggle = (e) => {
    const details = e.target;
    if (!details || !details.querySelectorAll) return;
    details.querySelectorAll('[data-bitco-clamp]').forEach(measureClamp);
  };

  let resizeRaf = null;
  const onResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(measureAllClamps);
  };

  const initSection = () => {
    if (atcObserver) atcObserver.disconnect();
    if (variantChangeUnsubscribe) {
      variantChangeUnsubscribe();
      variantChangeUnsubscribe = null;
    }

    const bar = getBar();
    const primaryBtn = getPrimaryBtn();
    if (!bar || !primaryBtn) return;

    primaryVisible = true;
    updateBarVisibility();

    atcObserver = new IntersectionObserver(onIntersect, { threshold: 0 });
    atcObserver.observe(primaryBtn);

    // subscribe/publish/PUB_SUB_EVENTS come from assets/pubsub.js + assets/constants.js, both
    // loaded globally (non-module classic scripts) in layout/theme.liquid ahead of this deferred
    // script — same globals assets/price-per-item.js already relies on. Feature-detected defensively.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        syncPriceFrom(event);
        syncSubmitState(event);
        // Choosing a COLOUR makes product-info.js re-render the picker from the server, and the
        // server always resolves a full variant — so a size comes back checked even though the
        // shopper only picked a colour. Clear it again unless they have genuinely chosen one
        // (clearPreselectedSize returns early once sizeChosen is set, so picking a size and then
        // switching colour keeps the size, which is what you want).
        clearPreselectedSize();
        // Dawn re-renders the picker on every swap; re-read the checked radios from the new DOM.
        syncStickyVariant();
        // …and that re-render replaces the <details> with a fresh server-rendered one, which is
        // always `open`. Without this the size grid springs back open the instant you pick a
        // size — collapsing it in onSizeChosen only ever touched the element about to be thrown
        // away.
        syncFold();
      });
    }

    clearPreselectedSize();
    syncFold();
    syncStickyVariant();
  };

  const onDocumentClick = (e) => {
    // `closest` is feature-checked because this branch runs on EVERY click on the page, ahead of
    // the sizePending guard that used to short-circuit before any DOM walk happened.
    const clampBtn = e.target.closest && e.target.closest('[data-bitco-clamp-toggle]');
    if (clampBtn) {
      toggleClamp(clampBtn);
      return;
    }

    // M1.1: hold both add buttons until a size exists. Capture-phase so this runs before Dawn's
    // product-form.js submit handler on the primary button.
    if (sizePending && e.target.closest('[data-bitco-atc-primary], [data-bitco-sticky-atc-submit]')) {
      e.preventDefault();
      e.stopPropagation();
      revealSizeGroup();
      return;
    }

    const btn = e.target.closest('[data-bitco-sticky-atc-submit]');
    if (!btn) return;

    const primaryBtn = getPrimaryBtn();
    if (!primaryBtn || primaryBtn.disabled) return;

    const form = primaryBtn.closest('form');
    if (form && typeof form.requestSubmit === 'function') form.requestSubmit(primaryBtn);
  };

  const onDocumentChange = (e) => {
    const radio = e.target.closest && e.target.closest('.bitco-variant-group input[type="radio"]');
    if (!radio) return;
    const group = radio.closest('.bitco-variant-group');
    if (group && group.querySelector('.bitco-variant-options--size')) onSizeChosen();
    syncStickyVariant();
  };

  const bindDocumentListenersOnce = () => {
    if (window.__bitcoProductDocBound) return;
    window.__bitcoProductDocBound = true;
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('change', onDocumentChange);
    document.addEventListener('toggle', onDocumentToggle, true);
    window.addEventListener('resize', onResize);
    mql.addEventListener('change', updateBarVisibility);
    foldMql.addEventListener('change', syncFold);
  };

  const bindSectionReloadListenerOnce = () => {
    if (window.__bitcoProductSectionListenerBound) return;
    window.__bitcoProductSectionListenerBound = true;
    document.addEventListener('shopify:section:load', () => {
      initSection();
      measureAllClamps();
    });
  };

  const init = () => {
    bindDocumentListenersOnce();
    bindSectionReloadListenerOnce();
    // Before initSection(), which bails early on any page without the sticky ATC bar — the
    // clamps must not be hostage to that.
    measureAllClamps();
    // Web fonts land after first paint and change how many lines the copy takes, so a block that
    // measured as "fits" at fallback-font metrics can overflow once Inter/Space Grotesk arrive.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureAllClamps);
    initSection();
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
